import React from 'react';

/**
 * 話者のアイコン (VI 定義 §10: 話者A `user-round`、話者B `user-round-search`)。
 *
 * アイコンのライブラリは入っていないため、lucide (ISC ライセンス) の同名アイコンの形を
 * インラインの SVG で書く (既存の CopyButton と同じくインライン SVG)。
 * 装飾のため読み上げない (役割はラベルの文字で伝える)。
 */

/*
 * 下の ICON_SHAPES の 3 つの形は、Lucide (https://github.com/lucide-icons/lucide) の同名のアイコン
 * (user-round・user-round-search・triangle-alert) の図形の定義を写したもの (Issue #1702)。
 * 2026-10-08 に、Lucide の main (b56741cf) の icons/<名前>.svg と一致することを確かめた。
 *
 * triangle-alert は、Lucide の LICENSE が「Feather に由来する」と挙げる alert-triangle の現在の名前
 * (Lucide の icons/triangle-alert.json の aliases) のため、ISC に加えて、後半の MIT (Feather) の表示も当たる。
 *
 * 次のコメントが、Lucide の LICENSE の全文 (上の main の LICENSE。変えずに写している。行頭の「 * 」だけを足した)。
 * ソースのコメントは、ビルドで取り除かれて配られる JS に残らない。**次のコメントの先頭の印 (アットマーク + license) は消さない**——
 * 印のあるコメントは、ビルドが配るファイル (build/static/js/main.*.js.LICENSE.txt) に抜き出される
 * (ISC・MIT とも、表示を「すべての複製」に含めることを求めるため。PM 判断 2026-10-08。Issue #1702)。
 * このコメントに印の文字列そのものを書かないのは、書くと、このコメントまで抜き出されるため。
 */

/**
 * @license
 * The icon shapes "user-round", "user-round-search" and "triangle-alert" in this file are copied from
 * Lucide (https://github.com/lucide-icons/lucide). The Lucide LICENSE follows, unmodified.
 *
 * ---------------------------------------------------------------------------
 * ISC License
 *
 * Copyright (c) 2026 Lucide Icons and Contributors
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * ---
 *
 * The following Lucide icons are derived from the Feather project:
 *
 * airplay, alert-circle, alert-octagon, alert-triangle, aperture, arrow-down-circle, arrow-down-left, arrow-down-right, arrow-down, arrow-left-circle, arrow-left, arrow-right-circle, arrow-right, arrow-up-circle, arrow-up-left, arrow-up-right, arrow-up, at-sign, calendar, cast, check, chevron-down, chevron-left, chevron-right, chevron-up, chevrons-down, chevrons-left, chevrons-right, chevrons-up, circle, clipboard, clock, code, columns, command, compass, corner-down-left, corner-down-right, corner-left-down, corner-left-up, corner-right-down, corner-right-up, corner-up-left, corner-up-right, crosshair, database, divide-circle, divide-square, dollar-sign, download, external-link, feather, frown, hash, headphones, help-circle, info, italic, key, layout, life-buoy, link-2, link, loader, lock, log-in, log-out, maximize, meh, minimize, minimize-2, minus-circle, minus-square, minus, monitor, moon, more-horizontal, more-vertical, move, music, navigation-2, navigation, octagon, pause-circle, percent, plus-circle, plus-square, plus, power, radio, rss, search, server, share, shopping-bag, sidebar, smartphone, smile, square, table-2, tablet, target, terminal, trash-2, trash, triangle, tv, type, upload, x-circle, x-octagon, x-square, x, zoom-in, zoom-out
 *
 * The MIT License (MIT) (for the icons listed above)
 *
 * Copyright (c) 2013-present Cole Bemis
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 * ---------------------------------------------------------------------------
 */
const ICON_SHAPES = {
    'user-round': (
        <>
            <circle cx="12" cy="8" r="5" />
            <path d="M20 21a8 8 0 0 0-16 0" />
        </>
    ),
    'user-round-search': (
        <>
            <circle cx="10" cy="8" r="5" />
            <path d="M2 21a8 8 0 0 1 10.434-7.62" />
            <circle cx="18" cy="18" r="3" />
            <path d="m22 22-1.9-1.9" />
        </>
    ),
    // 話者を判定できなかった発話の印・判定が不確実であることの表示 (DP-D-005・DP-D-009) の Warning のアイコン。
    // 仮定: アイコンは lucide の triangle-alert とする（SCR-D-001・VI 定義 §10 にアイコン名の定めが無い。未確認）
    'triangle-alert': (
        <>
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </>
    ),
};

function SpeakerIcon({ name }) {
    const shape = ICON_SHAPES[name];
    if (!shape) return null;
    return (
        <svg
            className="diar-speaker-icon"
            data-icon={name}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            {shape}
        </svg>
    );
}

export default SpeakerIcon;
